import cn from "classnames";
import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Menu.scss";
import { Link } from "react-router-dom";

import menuCloseIcon from "../../assets/images/menu__close-btn.png";
import { USER_ROLES } from "../../constants/common";
import { MENU } from "../../constants/menu";
import { MENU_FOOTER_LINKS } from "../../constants/router";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { clearClient } from "../../redux/clientSlice";
import { setExpandToolsMenu, setShowMenu } from "../../redux/windowSlice";

import { ClientMenu } from "./ClientMenu/ClientMenu";
import { CompanyAdminMenu } from "./CompanyAdminMenu/CompanyAdminMenu";
import { SuperUserMenu } from "./SuperUserMenu/SuperUserMenu";
import { TrainerMenu } from "./TrainerMenu/TrainerMenu";

export const Menu = () => {
  const dispatch = useDispatch();
  const showMenu = useSelector((state) => state.window.showMenu);
  const userRole = useSelector((state) => state.user.user?.role);
  const currentCompany = useSelector((state) => state.companies.currentCompany);
  const loadingUser = useSelector((state) => state.user.loading);
  const loadingCompanies = useSelector((state) => state.companies.loading);
  const clientId = useSelector((state) => state.user.user?.id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const closeMenu = (e) => {
    const name = e?.target?.getAttribute("name");

    if (MENU.includes(name)) return;

    document.body.removeAttribute("style");
    dispatch(setExpandToolsMenu(false));
    dispatch(setShowMenu(false));
  };

  const [_, setPreviousUrl] = useLocalStorage("prevUrl", "");

  useEffect(() => {
    if (
      window.location.pathname !== "/terms" &&
      window.location.pathname !== "/privacy"
    ) {
      setPreviousUrl(window.location.pathname);
    }
  }, [window.location.pathname]);

  const closeMenuAndResetEditableClientId = () => {
    closeMenu();
    dispatch(clearClient());
  };

  const closeMenuOnBtnClick = (e) => {
    if (e.key === "Escape") closeMenu();
  };

  useEffect(() => {
    document.addEventListener("keyup", closeMenuOnBtnClick);
    return () => {
      document.removeEventListener("keyup", closeMenuOnBtnClick);
    };
  }, []);

  const getMenu = useMemo(() => {
    const { CLIENT, COMPANY_ADMIN, TRAINER, SUPERUSER } = USER_ROLES;

    const menus = {
      [CLIENT]: (
        <ClientMenu key="client" clientId={clientId} clickHandler={closeMenu} />
      ),
      [SUPERUSER]: (
        <SuperUserMenu
          key="superuser"
          closeMenuAndResetEditableClientId={closeMenuAndResetEditableClientId}
          closeMenu={closeMenu}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
        />
      ),
      [COMPANY_ADMIN]: (
        <CompanyAdminMenu
          key="company-admin"
          closeMenuAndResetEditableClientId={closeMenuAndResetEditableClientId}
          closeMenu={closeMenu}
          isCanAddCoaches={currentCompany?.can_add_coaches}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
        />
      ),
      [TRAINER]: (
        <TrainerMenu
          key="trainer"
          closeMenuAndResetEditableClientId={closeMenuAndResetEditableClientId}
          closeMenu={closeMenu}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
        />
      ),
      default: (
        <SuperUserMenu
          key="default"
          closeMenuAndResetEditableClientId={closeMenuAndResetEditableClientId}
          closeMenu={closeMenu}
          showMenu={showMenu}
        />
      ),
    };

    return () => menus[userRole] || menus.default;
  }, [userRole, currentCompany?.can_add_coaches]);

  return (
    <div
      className={cn("menu__wrapper", { menu__wrapper_open: showMenu })}
      onClick={closeMenu}
    >
      <div
        className={cn("menu__inner-container", {
          "menu__inner-container_open": showMenu,
        })}
        name="inner-container"
      >
        <div className="menu" name="menu">
          <img
            className="menu__close-btn"
            src={menuCloseIcon}
            onClick={closeMenu}
            alt="Menu close icon"
            width="24"
            height="24"
          />
          {!(loadingUser || loadingCompanies) && getMenu()}
          <div className="menu__footer">
            {MENU_FOOTER_LINKS.map(({ text, link }, indx) => (
              <React.Fragment key={`${indx}-${text}`}>
                <div onClick={closeMenuAndResetEditableClientId}>
                  <Link to={link} className="menu__footer__link">
                    {text}
                  </Link>
                </div>
                <span> | </span>
              </React.Fragment>
            ))}
            <div>
              <a
                href="https://recomposer.com/knowledgebase"
                className="menu__footer__link"
              >
                help
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
